/* ---------------------------------------------------------------------------
 * Predictive search - locale-safe, cached, keyboard-navigable.
 *
 * Background
 * ----------
 * Shopify's /search/suggest endpoint answers 417 "Unsupported buyer locale"
 * on this shop's Arabic (primary) locale. The previous workaround pinned every
 * request to /en/search/suggest and then string-replaced "/en/" out of the
 * returned links, which is why an Arabic shopper searching a term was shown
 * English product titles.
 *
 * What happens now
 * ----------------
 * 1. Every request is built from the CURRENT locale root, never a hard-coded
 *    "/en/". Results are always in the language being browsed.
 * 2. On first focus we probe /search/suggest once. If the locale supports it we
 *    use it (full predictive features). If it answers 417 we transparently fall
 *    back to /search?section_id=predictive-search, which is locale-correct on
 *    every locale. The outcome is cached in sessionStorage, so it costs one
 *    tiny request per session - and the moment Shopify fixes the locale the
 *    site upgrades itself back with no code change.
 * 3. Category suggestions: the main menu is indexed once per session (see
 *    sections/search-collections-index.liquid) and matched locally, so typing
 *    "غرف نوم" offers the category itself, not 300 loose products.
 * 4. Results are re-ranked so products whose TITLE matches outrank products
 *    that only matched deep inside their description.
 * 5. Zero-result recovery: the query is retried with an Arabic-normalised form
 *    (diacritics, tatweel, alef/ya/ta-marbuta variants, Arabic-Indic digits)
 *    and then with the trailing word dropped, before giving up.
 * 6. Responses are cached in memory + sessionStorage, requests are debounced
 *    and aborted, and nothing is fetched below 2 characters - so this adds no
 *    cost to page load and fewer round trips than before.
 * ------------------------------------------------------------------------- */

const PS_SETTINGS = {
  minChars: 2,
  resultLimit: 8,
  categoryLimit: 5,
  cacheTtl: 5 * 60 * 1000,
  maxCacheEntries: 40,
  maxRecent: 6,
  recentKey: 'ps:recent',
  modeKeyPrefix: 'ps:mode:',
  categoryKeyPrefix: 'ps:cats:',
};

const PS_ARABIC = {
  diacritics: /[ؐ-ًؚ-ٰٟۖ-ۭ]/g,
  tatweel: /ـ/g,
  alef: /[آأإٱ]/g,
  alefMaqsura: /ى/g,
  taMarbuta: /ة/g,
  hamzaWaw: /ؤ/g,
  hamzaYa: /ئ/g,
  arabicDigits: /[٠-٩]/g,
  persianDigits: /[۰-۹]/g,
};

function psNormalize(term) {
  return term
    .replace(PS_ARABIC.diacritics, '')
    .replace(PS_ARABIC.tatweel, '')
    .replace(PS_ARABIC.alef, 'ا')
    .replace(PS_ARABIC.alefMaqsura, 'ي')
    .replace(PS_ARABIC.taMarbuta, 'ه')
    .replace(PS_ARABIC.hamzaWaw, 'و')
    .replace(PS_ARABIC.hamzaYa, 'ي')
    .replace(PS_ARABIC.arabicDigits, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
    .replace(PS_ARABIC.persianDigits, (d) => String.fromCharCode(d.charCodeAt(0) - 0x06f0 + 48))
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function psEscapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function psSafeStorage(area) {
  try {
    const store = window[area];
    const probe = '__ps__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch (error) {
    return null;
  }
}

const psSession = psSafeStorage('sessionStorage');
const psLocal = psSafeStorage('localStorage');

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.predictiveSearchContainer = this.querySelector('.predictive-search');

    this.calcModalTop = this.calcSearchModalTop.bind(this);

    // Locale-aware endpoints. Never hard-code a locale prefix here.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const base = root.replace(/\/$/, '');
    this.localeBase = base;
    this.searchUrl = this.dataset.searchUrl || `${base}/search`;
    this.predictiveUrl = this.dataset.predictiveUrl || `${base}/search/suggest`;
    this.localeKey = base || 'root';
    this.mode = psSession ? psSession.getItem(PS_SETTINGS.modeKeyPrefix + this.localeKey) : null;
    this.modeProbe = null;
    this.categories = null;
    this.categoriesLabel = '';
    this.categoriesPromise = null;

    // Note: the search field's text direction is handled in
    // assets/component-search.css, not here - see the "Search field direction"
    // block there. The markup's hard-coded dir="ltr" is overridden in CSS so
    // the field follows the storefront's own direction with no JS involved.

    this.calcHeight();
    this.setupEventListeners();
  }

  calcHeight() {
    this.results = document.querySelector('.predictive-search__result-group');
    this.resultsTabs = document.querySelector('.predictive-search__result-tabs');
    if (this.results && this.resultsTabs) {
      this.results.style.setProperty(
        '--search-height',
        `${parseInt(this.resultsTabs.getBoundingClientRect().bottom)}px`
      );

      const resultsWrapper = this.results.querySelector('.predictive-search-results-wrapper');
      if (resultsWrapper && resultsWrapper.scrollHeight > resultsWrapper.clientHeight) {
        resultsWrapper.classList.add('predictive-search-results-wrapper--scroll');
      } else if (resultsWrapper) {
        resultsWrapper.classList.remove('predictive-search-results-wrapper--scroll');
      }
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  /* ---------------------------------------------------------------- mode -- */

  /**
   * Decide once per session whether /search/suggest works on this locale.
   * Fired on focus so the answer is ready before the shopper finishes typing.
   */
  resolveMode() {
    if (this.mode) return Promise.resolve(this.mode);
    if (this.modeProbe) return this.modeProbe;

    const probeUrl = `${this.predictiveUrl}.json?q=a&resources[type]=query&resources[limit]=1`;

    this.modeProbe = fetch(probeUrl, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? 'predictive' : 'search'))
      .catch(() => 'search')
      .then((mode) => {
        this.mode = mode;
        if (psSession) psSession.setItem(PS_SETTINGS.modeKeyPrefix + this.localeKey, mode);
        return mode;
      });

    return this.modeProbe;
  }

  buildRequestUrl(term) {
    const params = new URLSearchParams();
    params.append('q', term);
    params.append('section_id', 'predictive-search');

    if (this.mode === 'predictive') {
      params.append('resources[type]', 'product');
      params.append('resources[limit]', String(PS_SETTINGS.resultLimit));
      params.append('resources[options][prefix]', 'last');
      params.append('resources[options][unavailable_products]', 'last');
      return `${this.predictiveUrl}?${params.toString()}`;
    }

    params.append('type', 'product');
    params.append('options[prefix]', 'last');
    params.append('options[unavailable_products]', 'last');
    return `${this.searchUrl}?${params.toString()}`;
  }

  /* ---------------------------------------------------------- categories -- */

  loadCategories() {
    if (this.categories) return Promise.resolve(this.categories);
    if (this.categoriesPromise) return this.categoriesPromise;

    const key = PS_SETTINGS.categoryKeyPrefix + this.localeKey;
    if (psSession) {
      try {
        const raw = psSession.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          this.categories = parsed.items || [];
          this.categoriesLabel = parsed.label || '';
          return Promise.resolve(this.categories);
        }
      } catch (error) {
        /* corrupted entry - refetch below */
      }
    }

    this.categoriesPromise = fetch(`${this.localeBase}/?section_id=search-collections-index`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        const node = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-search-collections]');
        const parsed = node ? JSON.parse(node.textContent) : { label: '', items: [] };
        this.categories = parsed.items || [];
        this.categoriesLabel = parsed.label || '';
        if (psSession) {
          try {
            psSession.setItem(key, JSON.stringify(parsed));
          } catch (error) {
            /* quota - the in-memory copy still applies */
          }
        }
        return this.categories;
      })
      .catch(() => {
        this.categories = [];
        return this.categories;
      });

    return this.categoriesPromise;
  }

  matchCategories(term) {
    const list = this.categories || [];
    const query = psNormalize(term);
    if (!list.length || query.length < PS_SETTINGS.minChars) return [];

    const tokens = query.split(' ').filter(Boolean);
    const scored = [];

    list.forEach((entry) => {
      const title = psNormalize(entry.t || '');
      const parent = psNormalize(entry.p || '');
      let score = 0;

      if (title === query) score = 100;
      else if (title.startsWith(query)) score = 80;
      else if (title.includes(query)) score = 60;
      else if (tokens.length > 1 && tokens.every((token) => title.includes(token))) score = 50;
      else if (parent.includes(query)) score = 25;

      if (score) scored.push({ entry, score });
    });

    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, PS_SETTINGS.categoryLimit)
      .map((match) => match.entry);
  }

  renderCategories(term) {
    const host = this.predictiveSearchResults;
    if (!host) return;
    host.querySelector('[data-ps-categories]')?.remove();

    const matches = this.matchCategories(term);
    if (!matches.length) return;

    const block = document.createElement('div');
    block.className = 'ps-categories';
    block.setAttribute('data-ps-categories', '');

    if (this.categoriesLabel) {
      const title = document.createElement('span');
      title.className = 'ps-categories__title h5';
      title.textContent = this.categoriesLabel;
      block.append(title);
    }

    const list = document.createElement('ul');
    list.className = 'ps-categories__list';

    matches.forEach((entry) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'ps-categories__link';
      link.href = entry.u;

      const label = document.createElement('span');
      label.className = 'ps-categories__link-title';
      label.textContent = entry.t;
      link.append(label);

      if (entry.p) {
        const parent = document.createElement('span');
        parent.className = 'ps-categories__link-parent';
        parent.textContent = entry.p;
        link.append(parent);
      }

      item.append(link);
      list.append(item);
    });

    block.append(list);
    host.prepend(block);
  }

  /* --------------------------------------------------------------- cache -- */

  cacheKey(term) {
    return `${this.localeKey}|${this.mode}|${term.toLowerCase()}`;
  }

  readCache(term) {
    const key = this.cacheKey(term);
    const memory = this.cachedResults[key];
    if (memory && Date.now() - memory.at < PS_SETTINGS.cacheTtl) return memory;

    if (psSession) {
      try {
        const raw = psSession.getItem(`ps:r:${key}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Date.now() - parsed.at < PS_SETTINGS.cacheTtl) {
            this.cachedResults[key] = parsed;
            return parsed;
          }
          psSession.removeItem(`ps:r:${key}`);
        }
      } catch (error) {
        /* corrupted entry - ignore */
      }
    }
    return null;
  }

  writeCache(term, entry) {
    const key = this.cacheKey(term);
    const keys = Object.keys(this.cachedResults);
    if (keys.length >= PS_SETTINGS.maxCacheEntries) delete this.cachedResults[keys[0]];

    this.allPredictiveSearchInstances.forEach((instance) => {
      instance.cachedResults[key] = entry;
    });

    if (psSession) {
      try {
        psSession.setItem(`ps:r:${key}`, JSON.stringify(entry));
      } catch (error) {
        /* quota - the in-memory cache still applies */
      }
    }
  }

  /* -------------------------------------------------------------- events -- */

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (newSearchTerm.length < PS_SETTINGS.minChars) {
      this.close(false);
      this.removeAttribute('loading');
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a') ||
      (event.submitter && event.submitter.classList.contains('card__link'))
    ) {
      event.preventDefault();
      return;
    }
    this.rememberTerm(this.getQuery());
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.resolveMode();
    this.loadCategories();
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderRecentSearches();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (!this.classList.contains('search-wrapper')) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close(true);
      });
    }
  }

  onKeydown(event) {
    if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') return;
    if (!this.getAttribute('open')) return;
    event.preventDefault();
    this.moveSelection(event.code === 'ArrowDown' ? 1 : -1);
  }

  onKeyup(event) {
    if (event.code === 'ArrowDown' || event.code === 'ArrowUp') return;
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
      case 'Escape':
        this.close(true);
        this.closeResults(true);
        break;
    }
  }

  /* ----------------------------------------------------------- selection -- */

  visibleOptions() {
    const activeList = this.querySelector('.predictive-search__results-list.active');
    if (!activeList) return [];
    return Array.from(activeList.querySelectorAll('[role="option"]'));
  }

  moveSelection(direction) {
    const options = this.visibleOptions();
    if (!options.length) return;

    const currentIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = options.length - 1;
    if (nextIndex >= options.length) nextIndex = 0;

    options.forEach((option) => option.setAttribute('aria-selected', 'false'));
    const next = options[nextIndex];
    next.setAttribute('aria-selected', 'true');
    next.scrollIntoView({ block: 'nearest' });
    if (next.id) this.input.setAttribute('aria-activedescendant', next.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      this.rememberTerm(this.getQuery());
      selectedOption.click();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;

    const matches = currentButtonText.match(new RegExp(psEscapeRegExp(previousTerm), 'g'));
    if (matches && matches.length > 1) return;

    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  /* ------------------------------------------------------------- fetching -- */

  /**
   * Query variants tried in order until one returns results:
   * the term as typed, its Arabic-normalised form, then progressively
   * shorter prefixes of the phrase.
   */
  buildVariants(term) {
    const variants = [term];
    const normalized = psNormalize(term);
    if (normalized && normalized !== term) variants.push(normalized);

    const words = (normalized || term).split(' ').filter(Boolean);
    for (let size = words.length - 1; size >= 1 && variants.length < 4; size -= 1) {
      const shorter = words.slice(0, size).join(' ');
      if (shorter && !variants.includes(shorter)) variants.push(shorter);
    }
    return variants;
  }

  fetchVariant(term) {
    const cached = this.readCache(term);
    if (cached) return Promise.resolve(cached);

    return fetch(this.buildRequestUrl(term), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        const markup = section ? section.innerHTML : '';
        const counter = section ? section.querySelector('[data-ps-count]') : null;
        const count = counter ? parseInt(counter.dataset.psCount, 10) || 0 : 0;

        const entry = { markup, count, term, at: Date.now() };
        this.writeCache(term, entry);
        return entry;
      });
  }

  async getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.setLiveRegionLoadingState();

    try {
      await this.resolveMode();

      const variants = this.buildVariants(searchTerm);
      let result = null;

      for (let index = 0; index < variants.length; index += 1) {
        result = await this.fetchVariant(variants[index]);
        if (result.count > 0) break;
      }

      // Never paint results for a query the shopper has already moved past.
      if (this.getQuery() !== searchTerm) return;

      this.renderSearchResults(result ? result.markup : '');
      this.clickSearchTabs();
      this.rankResults(searchTerm);
      this.highlightTerm(searchTerm);
      this.renderCategories(searchTerm);
      this.calcModalTop();
      this.calcHeight();

      try {
        colorSwatches();
      } catch (error) {
        /* optional theme helper */
      }
    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 20) return;
      this.removeAttribute('loading');
      this.close();
    }
  }

  /* ------------------------------------------------------------ rendering -- */

  /**
   * Shopify's search index also matches product descriptions, and on this shop
   * the descriptions cross-link other categories - so a product whose title
   * has nothing to do with the query can outrank an exact title match.
   * Re-order the returned cards so title matches come first; ties keep
   * Shopify's own relevance order.
   */
  rankResults(term) {
    const list = this.querySelector('.predictive-search__results-list_products');
    if (!list) return;

    const tokens = Array.from(new Set([term, psNormalize(term)].flatMap((value) => value.split(' '))))
      .map((token) => psNormalize(token))
      .filter((token) => token.length >= 2);
    if (!tokens.length) return;

    const items = Array.from(list.children);
    if (items.length < 2) return;

    const scored = items.map((element, index) => {
      const heading = element.querySelector('.card__title, .card__heading');
      const title = psNormalize(heading ? heading.textContent : '');
      let score = 0;
      tokens.forEach((token) => {
        if (title.includes(token)) score += 1;
      });
      return { element, score, index };
    });

    if (scored.every((entry) => entry.score === scored[0].score)) return;

    scored.sort((a, b) => b.score - a.score || a.index - b.index);
    scored.forEach((entry) => list.append(entry.element));
  }

  highlightTerm(term) {
    const tokens = Array.from(new Set([term, psNormalize(term)].flatMap((value) => value.split(' '))))
      .filter((token) => token.length >= 2)
      .sort((a, b) => b.length - a.length);
    if (!tokens.length) return;

    const pattern = new RegExp(`(${tokens.map(psEscapeRegExp).join('|')})`, 'gi');
    const headings = this.querySelectorAll('.predictive-search__results-list_products .card__heading');

    headings.forEach((heading) => {
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach((node) => {
        pattern.lastIndex = 0;
        if (!pattern.test(node.nodeValue)) return;
        pattern.lastIndex = 0;
        const fragment = document.createDocumentFragment();
        let cursor = 0;
        let match;
        while ((match = pattern.exec(node.nodeValue)) !== null) {
          fragment.append(node.nodeValue.slice(cursor, match.index));
          const mark = document.createElement('mark');
          mark.textContent = match[0];
          fragment.append(mark);
          cursor = match.index + match[0].length;
        }
        fragment.append(node.nodeValue.slice(cursor));
        node.replaceWith(fragment);
      });
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.calcTabsHeight();
  }

  calcTabsHeight() {
    const tabs = this.querySelector('.predictive-search__result-tabs');

    if (tabs) {
      const tabsHeight = tabs.getBoundingClientRect().height;
      this.style.setProperty('--tabs-height', `${tabsHeight}px`);
    }
  }

  calcSearchModalTop() {
    if (!this.predictiveSearchContainer) return;
    const clientY = this.predictiveSearchContainer.getBoundingClientRect().top;

    this.style.setProperty('--search-modal-top', `${clientY}px`);
  }

  clickSearchTabs() {
    const results = this.querySelectorAll('.predictive-search__results-list');
    if (!results.length) return;

    const firstTab = this.querySelector('.predictive-search__result-tab');
    results[0].classList.add('active');
    results[0].parentElement.classList.add('active');
    if (firstTab) firstTab.classList.add('active');

    this.querySelectorAll('.predictive-search__result-tab').forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();

        const typeTarget = tab.dataset.typeTarget;

        this.querySelectorAll('.predictive-search__result-tab').forEach((element) => {
          element.classList.remove('active');
        });

        tab.classList.add('active');

        results.forEach((element) => {
          if (element.dataset.type === typeTarget) {
            element.classList.add('active');
            element.parentElement.classList.add('active');
          } else {
            element.classList.remove('active');
            element.parentElement.classList.remove('active');
          }
        });
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const counter = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (counter) this.setLiveRegionText(counter.textContent);
  }

  /* ------------------------------------------------------ recent searches -- */

  rememberTerm(term) {
    if (!psLocal || !term || term.length < PS_SETTINGS.minChars) return;
    try {
      const stored = JSON.parse(psLocal.getItem(PS_SETTINGS.recentKey) || '[]');
      const next = [term, ...stored.filter((item) => item !== term)].slice(0, PS_SETTINGS.maxRecent);
      psLocal.setItem(PS_SETTINGS.recentKey, JSON.stringify(next));
    } catch (error) {
      /* storage unavailable - recents are a nicety, not a requirement */
    }
  }

  renderRecentSearches() {
    if (!psLocal) return;
    const host = this.querySelector('.predictive-search-initial-content');
    if (!host || host.querySelector('[data-ps-recent]')) return;

    let terms = [];
    try {
      terms = JSON.parse(psLocal.getItem(PS_SETTINGS.recentKey) || '[]');
    } catch (error) {
      return;
    }
    if (!terms.length) return;

    const block = document.createElement('div');
    block.className = 'predictive-search-popular';
    block.setAttribute('data-ps-recent', '');

    const list = document.createElement('ul');
    list.className = 'predictive-search-popular-list list-unstyled';

    terms.forEach((term) => {
      const item = document.createElement('li');
      item.className = 'predictive-search-popular-list__item';

      const link = document.createElement('a');
      link.className = 'predictive-search-popular-list__link';
      link.href = `${this.searchUrl}?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;

      const label = document.createElement('span');
      label.className = 'predictive-search-popular-list__link-title';
      label.textContent = term;

      link.append(label);
      item.append(link);
      list.append(item);
    });

    block.append(list);

    const main = host.querySelector('.predictive-search-main');
    if (main) {
      main.prepend(block);
    } else {
      host.prepend(block);
    }
  }

  /* --------------------------------------------------------- open / close -- */

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      if (this.resetBtn) this.resetBtn.removeAttribute('style');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
